import { useState } from "react";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api/httpClient";

/**
 * Shared manual/override underwriting dialog state + submit handlers for retrieve hooks.
 */
export function useBondRetrievalUnderwritingActions({
  bond_id,
  userdetails,
  setIsUnderwritingLoading,
}) {
  const [underwritingAnchorEl, setUnderwritingAnchorEl] = useState(null);
  const [overrideReasonDialogOpen, setOverrideReasonDialogOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [manualUnderwritingDialogOpen, setManualUnderwritingDialogOpen] = useState(false);
  const [manualUnderwritingReason, setManualUnderwritingReason] = useState("");

  const handleUnderwritingMenuClick = (event) => {
    setUnderwritingAnchorEl(event.currentTarget);
  };

  const handleUnderwritingMenuClose = () => {
    setUnderwritingAnchorEl(null);
  };

  const handleOverrideUnderwritingClick = () => {
    handleUnderwritingMenuClose();
    setOverrideReason("");
    setOverrideReasonDialogOpen(true);
  };

  const handleManualUnderwritingClick = () => {
    handleUnderwritingMenuClose();
    setManualUnderwritingReason("");
    setManualUnderwritingDialogOpen(true);
  };

  const handleSubmitManualUnderwriting = async () => {
    const reason = (manualUnderwritingReason || "").trim();
    if (!reason) {
      toast.error("Please enter a reason for creating the underwriting entry.");
      return;
    }
    try {
      setIsUnderwritingLoading(true);
      if (!userdetails?.first_name || !userdetails?.last_name) {
        throw new Error("User information is incomplete");
      }
      await apiClient(
        `/trigger_underwriting/${bond_id}/`,
        "POST",
        {
          underwriting_reason: reason,
          created_by: `${userdetails.first_name} ${userdetails.last_name}`,
        },
        {},
        { "Content-Type": "application/json" }
      );
      toast.success("Underwriting entry created successfully.");
      setManualUnderwritingDialogOpen(false);
      setManualUnderwritingReason("");
    } catch (error) {
      console.error("Create underwriting entry error:", error);
      const errorMessage =
        error.detail ||
        error.message ||
        error.response?.data?.detail ||
        "Failed to create underwriting entry. Please try again.";
      toast.error(errorMessage);
      if (error.response?.status === 401) return;
    } finally {
      setIsUnderwritingLoading(false);
    }
  };

  const handleSubmitOverrideReason = async () => {
    const reason = (overrideReason || "").trim();
    if (!reason) {
      toast.error("Please enter a reason for overriding underwriting.");
      return;
    }
    try {
      setIsUnderwritingLoading(true);
      if (!userdetails?.first_name || !userdetails?.last_name) {
        throw new Error("User information is incomplete");
      }
      await apiClient(
        `/override_underwriting/${bond_id}/`,
        "POST",
        {
          overrided_by: `${userdetails.first_name} ${userdetails.last_name}`,
          override_reason: reason,
        },
        {},
        { "Content-Type": "application/json" }
      );
      toast.success("Underwriting overridden successfully");
      setOverrideReasonDialogOpen(false);
      setOverrideReason("");
    } catch (error) {
      console.error("Override underwriting error:", error);
      const errorMessage =
        error.detail ||
        error.message ||
        error.response?.data?.detail ||
        "Failed to override underwriting. Please try again.";
      toast.error(errorMessage);
      if (error.response?.status === 401) return;
    } finally {
      setIsUnderwritingLoading(false);
    }
  };

  return {
    underwritingAnchorEl,
    setUnderwritingAnchorEl,
    overrideReasonDialogOpen,
    setOverrideReasonDialogOpen,
    overrideReason,
    setOverrideReason,
    manualUnderwritingDialogOpen,
    setManualUnderwritingDialogOpen,
    manualUnderwritingReason,
    setManualUnderwritingReason,
    handleUnderwritingMenuClick,
    handleUnderwritingMenuClose,
    handleOverrideUnderwritingClick,
    handleManualUnderwritingClick,
    handleSubmitManualUnderwriting,
    handleSubmitOverrideReason,
  };
}

export default useBondRetrievalUnderwritingActions;
